import { NgTemplateOutlet } from '@angular/common';
import { Component, inject, ChangeDetectionStrategy, computed } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService } from 'primeng/dynamicdialog';

import { AuthService } from '../../services/auth/auth.service';
import { ProfileMenuService } from '../../services/profile/profile.service';
import { SidebarService } from '../../services/sidebar/sidebar.service';
import { MenuItem } from '../../models/menu.routes';
import { LoginComponent } from 'src/app/module/auth/pages/login/login.component';

@Component({
  selector: 'app-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    ButtonModule,
    RippleModule,
    AvatarModule,
    TooltipModule,
    NgTemplateOutlet
]
})
export class SideBarComponent {
  private readonly router = inject(Router);
  public readonly auth = inject(AuthService);
  private readonly dialogService = inject(DialogService);
  private readonly profile = inject(ProfileMenuService);
  public readonly sidebarService = inject(SidebarService);

  // Estado via Service
  readonly isOpen = this.sidebarService.isOpen;
  readonly isPushMode = this.sidebarService.isPushMode;
  
  // Dados derivados
  readonly isLogged = computed(() => this.auth.isLogged());
  readonly user = computed(() => this.auth.userDetails());
  readonly currentYear = new Date().getFullYear();
  readonly menuGroups = computed(() => {
    // Registrar dependência para recalcular quando o estado de login/usuário mudar
    this.auth.isLogged(); 
    return this.profile.getSidebarOptions();
  });
  closeSidebar(): void {
    this.sidebarService.close();
  }

  logout(): void {
    this.profile.logout();
  }

  openLoginModal(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const returnUrl = this.router.url;
    this.dialogService.open(LoginComponent, {
      header: 'Login',
      width: 'min(92vw, 500px)',
      height: 'auto',
      modal: true,
      closable: true,
      closeOnEscape: true,
      dismissableMask: true,
      showHeader: true,
      styleClass: 'login-modal-custom',
      style: {
        'max-width': '500px',
        'border-radius': '12px',
      },
      contentStyle: {
        overflow: 'hidden',
        display: 'flex',
        'flex-direction': 'column',
        'min-height': '0',
      },
      position: 'center',
      data: { returnUrl },
    });
  }

  handleItemClick(item: MenuItem, navigationHandled = false, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (item.command) {
      item.command({ item });
    } else if (item.routerLink && !navigationHandled) {
      const navigationExtras = item.queryParams ? { queryParams: item.queryParams } : {};
      this.router.navigate(item.routerLink, navigationExtras);
    }
    
    if (!this.isPushMode()) {
      this.sidebarService.close();
    }
  }

  formatUserName(name: string): string {
    return name ? name.split(' ')[0] : '';
  }

  isActiveRoute(route?: string): boolean {
    return !!route && this.router.url.split('?')[0].startsWith(route);
  }
}
